import { createContext, useContext, useRef, useState } from "react";
import BuilderRightSideBar from "../../Layouts/BuilderRightSideBar";
import Popover from "../../Layouts/Popover/Popover";
import ContextMenuContent from "./ContextMenuContent/ContextMenuContent";
import "./FlexibleLayout.css";
import PositionElementGuidLines from "./Guidelines/PositionElementGuidLines";
import KeyboardHandler from "./KeyboardHandler/KeyboardHandler";
import LayoutRendererOuter from "./LayoutRenderer/LayoutRendererOuter";
import useSaveConfig from "./PositionedHooks/useSaveConfig";
import TreeView from "./PositionedTreeView/TreeView";
import { useContextMenu, useDrag, useDragMeta, useResize, useSelection } from "./PositionedHooks/useDragHandler";

const PositionedScreenContext = createContext();

export const usePositionedScreenContext = () => useContext(PositionedScreenContext);

const PositionedScreen = ({
  layoutConfig,
  setLayoutConfig,
  setDraggedItem,
  draggedItem,
  setDragOffset,
  dragOffset,
  openEditBar,
  closeEditBar,
  screenWidth = 1600,
  selectedItem,
  setSelectedItem,
  type, // view, editable, template_selector
  gridSize,
  showTree = false,
  setShowTree,
  showGrid = true,
  device = "desktop",
  containerClass = "",
  scaleFactor,
  tableId,
  tableType,
  openMultiFieldEditBar
}) => {
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [guidlineData, setGuidlineData] = useState(null);
  const [selectedItemPath, setSelectedItemPath] = useState(null);
  const [hoveredDirectItemId, setHoveredDirectItemId] = useState(null);
  const [multiSelectedItems, setMultiSelectedItems] = useState(null);
  const containerRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const elementRefs = useRef({}); // key: elementId, value: React ref

  const onClickElement = (...args) => {
    if (type !== "editable") return;
    openEditBar(...args);
  };

  // 1️⃣ Hook 1: Drag Metadata (required for drag operations)
  const dragMeta = useDragMeta(containerRef, screenWidth);

  // 2️⃣ Hook 2: Drag & Drop Operations
  const { handleDragStart, handleDragOver, handleOnDrop } = useDrag({
    isEditable: () => type === "editable",
    draggedItem,
    dragOffset,
    layoutConfig,
    setLayoutConfig,
    selectedItem,
    setSelectedItem,
    setDraggedItem,
    setDragOffset,
    setHoveredItemId,
    setGuidlineData,
    setMultiSelectedItems,
    closeEditBar,
    gridSize,
    scaleFactor,
    containerRef,
    dragMeta // Pass the dragMeta from hook 1
  });

  // 3️⃣ Hook 3: Resize Operations
  const { handleResizeStart } = useResize({
    isEditable: () => type === "editable",
    setLayoutConfig,
    setGuidlineData,
    containerRef,
    screenWidth,
    gridSize
  });

  // 4️⃣ Hook 4: Selection Operations (Click handling)
  const { handleClick } = useSelection({
    isEditable: () => type === "editable",
    type,
    setSelectedItem,
    setSelectedItemPath,
    setMultiSelectedItems,
    closeEditBar,
    openEditBar
  });

  // 5️⃣ Hook 5: Context Menu Operations (Right-click)
  const { handleRightClick } = useContextMenu({
    closeEditBar,
    setMultiSelectedItems,
    setSelectedItem,
    setSelectedItemPath,
    setContextMenu
  });
  const { saveEditElement } = useSaveConfig({
    layoutConfig,
    setLayoutConfig
  });
  return (
    <PositionedScreenContext.Provider
      value={{
        setDraggedItem,
        setDragOffset,
        draggedItem,
        dragOffset,
        layoutConfig,
        setLayoutConfig,
        selectedItem,
        setSelectedItem,
        gridSize,
        type,
        hoveredItemId,
        setHoveredItemId,
        guidlineData,
        setGuidlineData,
        onClickElement,
        handleRightClick,
        closeEditBar,
        openEditBar,
        openMultiFieldEditBar,
        handleDragStart,
        handleDragOver,
        handleOnDrop,
        handleResizeStart,
        handleClick,
        saveEditElement,
        selectedItemPath,
        setSelectedItemPath,
        hoveredDirectItemId,
        setHoveredDirectItemId,
        multiSelectedItems,
        setMultiSelectedItems,
        elementRefs,
        tableId,
        tableType
      }}
    >
      <>
        {type !== "view" && <KeyboardHandler containerRef={containerRef} screenWidth={screenWidth} />}
        <div
          style={{
            position: "relative",
            minHeight: "100%",
            width: `${screenWidth}px`,
            margin: "auto",
            zoom: scaleFactor
          }}
          className={`z-screen ${containerClass}`}
          onDrop={(e) => handleOnDrop(e, "outer")}
          onDragOver={(e) => handleDragOver(e, "outer")}
          onMouseOut={() => setHoveredDirectItemId(null)}
        >
          {showGrid && (
            <div
              className="layout-container-layover"
              style={{
                height: "100%",
                width: `${screenWidth}px`,
                backgroundSize: `${type === "editable" ? `${gridSize}px ${gridSize}px` : ""}`
              }}
            ></div>
          )}
          <div
            style={{
              height: "100%",
              width: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              paddingBottom: type === "editable" ? "200px" : "0",
              boxSizing: "border-box",
              position: "relative"
            }}
            ref={containerRef}
          >
            <LayoutRendererOuter
              hoveredItemId={hoveredItemId}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleOnDrop={handleOnDrop}
              handleResizeStart={handleResizeStart}
              handleClick={handleClick}
              saveEditElement={saveEditElement}
              onClickElement={onClickElement}
              handleRightClick={handleRightClick}
              layoutConfig={layoutConfig}
              draggedItem={draggedItem}
              selectedItem={selectedItem}
              multiSelectedItems={multiSelectedItems}
              hoveredDirectItemId={hoveredDirectItemId}
              setHoveredDirectItemId={setHoveredDirectItemId}
              type={type}
              device={device}
            />
          </div>
          <PositionElementGuidLines guidlineData={guidlineData} />
          {contextMenu && (
            <Popover anchorPoint={contextMenu.anchorPoint} onClose={() => setContextMenu(null)}>
              <ContextMenuContent item={contextMenu} onClose={() => setContextMenu(null)} />
            </Popover>
          )}
        </div>

        {type === "editable" && (
          <BuilderRightSideBar
            isOpen={showTree}
            onClose={() => setShowTree(false)}
            heading="Tree"
            size={"small"}
            style={{ overflow: "scroll", left: "0px", right: "unset" }}
          >
            <TreeView />
          </BuilderRightSideBar>
        )}
      </>
    </PositionedScreenContext.Provider>
  );
};

export default PositionedScreen;
